/**
 * Panneaux Rencontre de rentrée : présences à l'entrée, sondage de décembre, rappel J+7.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { UserCheck, ChatCenteredText, BellRinging } from "@phosphor-icons/react";

export function AttendancePanel({ onSendAbsents }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const load = () => api.get("/rentree/attendance").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (item) => {
    try {
      await api.post("/rentree/attendance", { email: item.email, present: !item.present });
      setData((d) => ({ ...d, present: d.present + (item.present ? -1 : 1), items: d.items.map((i) => (i.email === item.email ? { ...i, present: !i.present } : i)) }));
    } catch { toast.error("Erreur"); }
  };
  if (!data) return null;
  const items = data.items.filter((i) => !q || (i.name || "").toLowerCase().includes(q.toLowerCase()) || i.email.includes(q.toLowerCase()));
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 print:hidden" data-testid="rentree-attendance">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="text-base md:text-lg font-bold text-zinc-100 flex items-center gap-2"><UserCheck size={18} weight="duotone" className="text-amber-400" /> Présences à l'entrée</h2>
        <div className="text-2xl font-black text-amber-300" data-testid="attendance-count">{data.present}<span className="text-zinc-500 text-sm"> / {data.total}</span></div>
      </div>
      <div className="flex gap-2 mb-2">
        <button onClick={() => setOpen(!open)} className="text-xs px-3 py-1.5 rounded border border-zinc-700 text-zinc-300 hover:border-amber-400" data-testid="attendance-toggle-list">{open ? "Fermer la liste" : "Cocher les familles présentes"}</button>
        <button onClick={onSendAbsents} className="text-xs px-3 py-1.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="send-summary-absents-btn">📝 Résumé aux {data.total - data.present} absents</button>
      </div>
      {open && (
        <>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un nom…" className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 mb-2" data-testid="attendance-search" />
          <ul className="max-h-72 overflow-auto divide-y divide-zinc-800/70" data-testid="attendance-list">
            {items.map((i) => (
              <li key={i.email}>
                <label className="flex items-center gap-3 py-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={i.present} onChange={() => toggle(i)} className="accent-emerald-500 w-4 h-4" data-testid={`attendance-${i.email}`} />
                  <span className={i.present ? "text-emerald-300" : "text-zinc-200"}>{i.name || i.email}</span>
                  <span className="text-zinc-600 text-xs ml-auto truncate max-w-[45%]">{i.email}</span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function SurveyPanel({ onPreview }) {
  const [res, setRes] = useState(null);
  const [show, setShow] = useState(false);
  const load = () => api.get("/rentree/survey/responses").then((r) => setRes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 print:hidden" data-testid="rentree-survey">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="text-base md:text-lg font-bold text-zinc-100 flex items-center gap-2"><ChatCenteredText size={18} weight="duotone" className="text-amber-400" /> Consultation de décembre</h2>
        <div className="text-2xl font-black text-amber-300" data-testid="survey-count">{res?.total ?? 0}<span className="text-zinc-500 text-sm"> réponses</span></div>
      </div>
      <p className="text-xs text-zinc-400 mb-2">6 questions (ce qui fonctionne, difficultés plateforme, communications, événements, améliorations, sujets pour janvier). Page publique : <a href="/sondage-membres" target="_blank" rel="noreferrer" className="text-amber-300 underline" data-testid="survey-public-link">/sondage-membres</a></p>
      <div className="flex flex-wrap gap-2">
        <button onClick={onPreview} className="text-xs px-3 py-1.5 rounded border border-violet-500/40 text-violet-300 hover:bg-violet-500/10" data-testid="preview-survey-btn">✉️ Envoyer l'invitation au sondage</button>
        <button onClick={() => { setShow(!show); load(); }} className="text-xs px-3 py-1.5 rounded border border-zinc-700 text-zinc-300" data-testid="survey-show-responses">{show ? "Masquer" : "Voir les réponses"}</button>
      </div>
      {show && res && (
        <div className="mt-3 space-y-3 max-h-80 overflow-auto" data-testid="survey-responses">
          {res.items.length === 0 && <div className="text-xs text-zinc-500">Aucune réponse pour l'instant.</div>}
          {res.items.map((r) => (
            <div key={r.id} className="rounded-lg border border-zinc-800 p-3 text-xs">
              <div className="text-zinc-500 mb-1">{r.created_at.slice(0, 10)} · {r.role || "—"} · {r.email || "anonyme"}</div>
              {res.questions.filter((qq) => r.answers[qq.id]).map((qq) => <div key={qq.id} className="mt-1"><span className="text-amber-300">{qq.q}</span><div className="text-zinc-200">{r.answers[qq.id]}</div></div>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReminderPanel({ cfg, onDone }) {
  const [sms, setSms] = useState(null);
  useEffect(() => { api.post("/rentree/send-family-sms").then((r) => setSms(r.data)).catch(() => {}); }, [cfg.family_sms_sent_at]);
  const sendSms = async () => {
    if (!window.confirm(`Envoyer maintenant le SMS de rappel à environ ${sms?.approx_targets ?? "?"} familles sans confirmation ?`)) return;
    try { await api.post("/rentree/send-family-sms?confirm=true"); toast.success("📱 SMS en cours d'envoi"); onDone && onDone(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const send = async () => {
    try {
      const p = await api.post("/rentree/send-reminder-now");
      if (!window.confirm(`Envoyer maintenant le rappel à ${p.data.targets} famille(s) sans les 5 actions confirmées ?`)) return;
      const r = await api.post("/rentree/send-reminder-now?confirm=true"); toast.success(`🔔 Rappel en cours d'envoi à ${r.data.targets} famille(s)`); onDone && onDone();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 print:hidden" data-testid="rentree-reminder">
      <h2 className="text-base md:text-lg font-bold text-zinc-100 flex items-center gap-2 mb-1"><BellRinging size={18} weight="duotone" className="text-amber-400" /> Rappel J+7 automatique</h2>
      <p className="text-xs text-zinc-400">7 jours après l'envoi du résumé, un courriel de rappel part automatiquement aux familles sans les 5 actions confirmées (une seule fois).</p>
      <div className="text-xs mt-2" data-testid="reminder-status">
        {cfg.reminder_sent_at ? <span className="text-emerald-400">✓ Rappel envoyé le {cfg.reminder_sent_at.slice(0, 10)} ({cfg.reminder_sent_count ?? 0} famille(s))</span>
          : cfg.summary_sent_at ? <span className="text-amber-300">Programmé : 7 jours après le {cfg.summary_sent_at.slice(0, 10)}</span>
          : <span className="text-zinc-500">S'activera après l'envoi du résumé.</span>}
      </div>
      <button onClick={send} className="mt-2 text-xs px-3 py-1.5 rounded border border-zinc-700 text-zinc-300 hover:border-amber-400" data-testid="reminder-now-btn">Envoyer le rappel maintenant</button>
      <div className="mt-4 pt-3 border-t border-zinc-800" data-testid="family-sms">
        <div className="text-sm font-bold text-zinc-100">📱 SMS familles — 3 jours avant la date limite</div>
        <div className="text-xs mt-1" data-testid="family-sms-status">
          {cfg.family_sms_sent_at ? <span className="text-emerald-400">✓ Envoyé le {cfg.family_sms_sent_at.slice(0, 10)} ({cfg.family_sms_stats?.sent ?? 0} SMS)</span>
            : sms?.sms_day ? <span className="text-amber-300">Programmé le {sms.sms_day} · ≈ {sms.approx_targets} familles sans confirmation</span>
            : <span className="text-zinc-500">Date limite non reconnue — utilise « 30 septembre 2026 ».</span>}
        </div>
        <button onClick={sendSms} className="mt-2 text-xs px-3 py-1.5 rounded border border-zinc-700 text-zinc-300 hover:border-amber-400" data-testid="family-sms-now-btn">Envoyer le SMS maintenant</button>
      </div>
    </div>
  );
}
